import React, { useEffect, useState } from "react";
import axiosInstance from "../../interceptor/axiosInstance";
import { FaArrowUp, FaArrowDown, FaCrown, FaCalendarAlt, FaMoneyBillWave, FaTimes, FaCheckCircle } from "react-icons/fa";
import "./Plans.css";
import "./Org.css";

const Plans = () => {
    const [plans, setPlans] = useState([]);
    const [activePlans, setActivePlans] = useState([]);
    const [selectedPlan, setSelectedPlan] = useState(null); 
    const [showModal, setShowModal] = useState(false);

    useEffect(() => {
        fetchPlans();
        fetchActivePlans();
    }, []);

    const fetchPlans = async () => {
        try {
            const response = await axiosInstance.get("/org/plans");
            setPlans(response.data);
        } catch (error) {
            console.error("Error fetching plans:", error);
        }
    };

    const fetchActivePlans = async () => {
        try {
            const response = await axiosInstance.get(`/org/current-plans/`);
            setActivePlans(response.data);
        } catch (error) {
            console.error("Error fetching active plans:", error);
        }
    };

    const handleSubscribe = async () => {
        if (!selectedPlan) return;
        try {
            await axiosInstance.post("/org/subscribe", { planId: selectedPlan.id });
            setShowModal(false);
            fetchActivePlans();
        } catch (error) {
            console.error("Error subscribing to plan:", error);
        }
    };

    const openModal = (plan) => {
        setSelectedPlan(plan);
        setShowModal(true);
    };

    return (
        <div className="plans-page">
            <div className="bg-orbs">
                <div className="orb-1"></div>
                <div className="orb-2"></div>
            </div>

            <div className="container" style={{ position: 'relative', zIndex: 2 }}>
                <h2 className="plans-title">Subscription Plans</h2>

                {/* Current & Next Plan Section */}
                {activePlans.length > 0 && (
                    <div className="row justify-content-center mb-4">
                        {activePlans.map((plan, index) => (
                            <div key={plan.plan_id} className="col-md-5 mb-3">
                                <div className={`current-plan-card ${index === 0 ? "current-plan" : "next-plan"}`}>
                                    <h3>
                                        {index === 0 ? "Current Plan" : "Next Plan"}:  
                                        <span className="badge bg-light text-dark ms-2">{plan.name}</span>
                                        {index === 0 && <FaCrown className="ms-2 text-warning" />}
                                    </h3>
                                    <p className="plan-description">{plan.description}</p>
                                    <div className="plan-price-label">
                                        Price: ${plan.price} / {plan.duration_days} days
                                    </div>
                                    <p className="plan-expiry-label">
                                        {index === 0 ? "Expires On" : "Starts On"}: {new Date(plan.end_date).toLocaleDateString()}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Upgrade/Downgrade Plans */}
                <h4 className="plans-grid-title">Upgrade or Downgrade Your Plan</h4>
                <div className="row justify-content-center">
                    {plans.map(plan => {
                        const isCurrentPlan = activePlans.some(p => p.plan_id === plan.id);
                        const isUpgrade = activePlans.length > 0 && plan.priority > activePlans[0].priority;
                        const isDowngrade = activePlans.length > 0 && plan.priority < activePlans[0].priority;

                        return (
                            <div key={plan.id} className="col-md-4 p-3">
                                <div className={`plan-card p-4 ${isCurrentPlan ? "is-active-plan" : ""}`}>
                                    <h5 className="font-outfit" style={{ fontSize: '20px', fontWeight: 700, color: 'var(--text-main)', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        {plan.name} {isCurrentPlan && <FaCrown className="text-warning" />}
                                    </h5>
                                    <p className="plan-description" style={{ minHeight: '44px' }}>{plan.description}</p>
                                    
                                    <div style={{ fontSize: '15px', color: 'var(--text-main)', margin: '14px 0' }}>
                                        <div style={{ marginBottom: '4px' }}><strong>Price:</strong> ${plan.price}</div>
                                        <div><strong>Duration:</strong> {plan.duration_type === 'months' ? `${plan.duration_days} months` : `${plan.duration_days} days`}</div>
                                    </div>

                                    <h6 className="font-outfit" style={{ fontSize: '14px', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: '18px', marginBottom: '8px' }}>
                                        Features
                                    </h6>
                                    <ul className="plan-features-list">
                                        {plan.settings.map(setting => (
                                            <li key={setting.key}>
                                                <span>{setting.description}</span>
                                                <span className="feature-badge">{setting.value}</span>
                                            </li>
                                        ))}
                                    </ul>

                                    {/* Subscribe Button */}
                                    <button 
                                        className={`btn-color mt-3 w-100 ${isCurrentPlan ? "disabled" : ""}`}
                                        onClick={() => openModal(plan)}
                                        disabled={isCurrentPlan}
                                        style={isCurrentPlan ? { background: '#cbd5e1', color: '#94a3b8', boxShadow: 'none' } : {}}
                                    >
                                        {isCurrentPlan ? "Current Plan" : (activePlans.length === 0) ? "Subscribe" : isUpgrade ? <>Upgrade <FaArrowUp style={{ marginLeft: '4px' }}/></> : <>Downgrade <FaArrowDown style={{ marginLeft: '4px' }}/></>}
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Custom Confirmation Modal */}
                {showModal && selectedPlan && (
                    <div className="custom-modal">
                        <div className="modal-content">
                            <h4 className="modal-title">Confirm Subscription Change</h4>
                            {activePlans.length === 0 ? (
                                <p className="modal-body-text">
                                    You are not on any active plan. You are going to subscribe to the <strong className="text-primary">{selectedPlan.name}</strong> plan now.
                                </p>
                            ) : (
                                <p className="modal-body-text">
                                    You are currently on the <strong className="text-primary">{activePlans[0].name}</strong> plan. 
                                    Your new plan <strong className="text-success">{selectedPlan.name}</strong> will start on 
                                    <strong> {new Date(activePlans[0].end_date).toLocaleDateString()} </strong> after your current plan expires.
                                </p>
                            )}

                            <div className="modal-actions">
                                <button className="btn-color" style={{ background: '#e2e8f0', color: '#475569 !important', boxShadow: 'none' }} onClick={() => setShowModal(false)}>
                                    <FaTimes style={{ marginRight: '6px' }} /> Cancel
                                </button>
                                <button className="btn-color" onClick={handleSubscribe}>
                                    <FaCheckCircle style={{ marginRight: '6px' }} /> Confirm
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Plans;
